import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Image from '../../../components/AppImage';

const UserTable = ({ 
  users, 
  selectedUsers, 
  onSelectUser, 
  onSelectAll, 
  onEditUser, 
  onViewUser,
  onResetPassword,
  onToggleStatus,
  sortConfig,
  onSort 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success text-success-foreground', label: 'Active' },
      inactive: { color: 'bg-muted text-muted-foreground', label: 'Inactive' },
      suspended: { color: 'bg-error text-error-foreground', label: 'Suspended' },
      pending: { color: 'bg-warning text-warning-foreground', label: 'Pending' }
    };
    
    const config = statusConfig?.[status] || statusConfig?.inactive;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { color: 'bg-primary text-primary-foreground', label: 'Admin' },
      manager: { color: 'bg-accent text-accent-foreground', label: 'Manager' },
      employee: { color: 'bg-secondary text-secondary-foreground', label: 'Employee' },
      contractor: { color: 'bg-muted text-muted-foreground', label: 'Contractor' },
      intern: { color: 'bg-warning text-warning-foreground', label: 'Intern' }
    };
    
    const config = roleConfig?.[role] || roleConfig?.employee;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatLastLogin = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const loginDate = new Date(date);
    const diffInHours = Math.floor((now - loginDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return loginDate?.toLocaleDateString();
  };

  // Pagination
  const totalPages = Math.ceil(users?.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const currentUsers = users?.slice(startIndex, endIndex);

  const isAllSelected = currentUsers?.length > 0 && currentUsers?.every(user => selectedUsers?.includes(user?.id));
  const isIndeterminate = currentUsers?.some(user => selectedUsers?.includes(user?.id)) && !isAllSelected;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="p-4 text-left">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => onSelectAll(e?.target?.checked, currentUsers)}
                />
              </th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>User</span>
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('department')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Department</span>
                  <Icon name={getSortIcon('department')} size={14} />
                </button>
              </th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('role')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Role</span>
                  <Icon name={getSortIcon('role')} size={14} />
                </button>
              </th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Status</span>
                  <Icon name={getSortIcon('status')} size={14} />
                </button>
              </th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('lastLogin')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Last Login</span>
                  <Icon name={getSortIcon('lastLogin')} size={14} />
                </button>
              </th>
              <th className="p-4 text-right text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers?.map((user) => (
              <tr key={user?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="p-4">
                  <Checkbox
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={(e) => onSelectUser(user?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
                      <Image
                        src={user?.avatar}
                        alt={user?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{user?.name}</p>
                      <p className="text-sm text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-foreground">{user?.department}</span>
                </td>
                <td className="p-4">
                  {getRoleBadge(user?.role)}
                </td>
                <td className="p-4">
                  {getStatusBadge(user?.status)}
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">
                    {formatLastLogin(user?.lastLogin)}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewUser(user)}
                      iconName="Eye"
                      iconSize={16}
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditUser(user)}
                      iconName="Edit"
                      iconSize={16}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onResetPassword(user)}
                      iconName="Key"
                      iconSize={16}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToggleStatus(user)}
                      iconName={user?.status === 'active' ? 'UserX' : 'UserCheck'}
                      iconSize={16}
                    >
                      {user?.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4 p-4">
        {currentUsers?.map((user) => (
          <div key={user?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedUsers?.includes(user?.id)}
                  onChange={(e) => onSelectUser(user?.id, e?.target?.checked)}
                />
                <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
                  <Image
                    src={user?.avatar}
                    alt={user?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-medium text-foreground">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              {getStatusBadge(user?.status)}
            </div>
            
            <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
              <div>
                <span className="text-muted-foreground">Department:</span>
                <span className="ml-1 text-foreground">{user?.department}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Role:</span>
                <span className="ml-1">{getRoleBadge(user?.role)}</span>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Last Login:</span>
                <span className="ml-1 text-foreground">{formatLastLogin(user?.lastLogin)}</span>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewUser(user)}
                iconName="Eye"
                iconSize={14}
              >
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditUser(user)}
                iconName="Edit"
                iconSize={14}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResetPassword(user)}
                iconName="Key"
                iconSize={14}
              >
                Reset
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onToggleStatus(user)}
                iconName={user?.status === 'active' ? 'UserX' : 'UserCheck'}
                iconSize={14}
              >
                {user?.status === 'active' ? 'Deactivate' : 'Activate'}
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t border-border p-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, users?.length)} of {users?.length} users
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              iconName="ChevronLeft"
              iconSize={16}
            >
              Previous
            </Button>
            <span className="text-sm text-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              iconName="ChevronRight"
              iconSize={16}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTable;